import { normalizePost } from "./classification";
import { canViewCooperationRequest, labelForRequestType } from "./cooperation";
import { getFirmPracticeAreas, hasTechnicalRole } from "./domain";
import { governanceContext } from "./governanceDemo";
import { canViewWorkspaceDiscussion, createMyWorkspaceViewModel } from "./myWorkspace";
import type {
  ActionItem,
  ActivityEvent,
  ActivityFeedItem,
  BottomActionChip,
  DashboardViewModel,
  Discussion,
  GovernancePulse,
  LexData,
  RecommendedItem,
  RecommendationTargetType,
  SidebarCounts,
} from "./types";

const day = 24 * 60 * 60 * 1000;
const responseCooperationStatuses = ["sent", "viewed"];
const conflictCooperationStatuses = ["awaiting_conflict_check", "conflict_check_requested"];

export function createDashboardViewModel(data: LexData, now = Date.now()): DashboardViewModel {
  const workspace = createMyWorkspaceViewModel(data, now);
  const urgentActions = workspace.action_items.filter((item) => isUrgentAction(item, now));
  const actionSummary = {
    actions_due_count: workspace.actions_due_count,
    open_votes_count: workspace.open_votes_count,
    requested_views_count: workspace.action_items.filter((item) => item.type === "View requested").length,
    cooperation_requests_count: workspace.cooperation_requests_count,
    drafts_count: workspace.drafts_count,
    urgent_count: urgentActions.length,
  };
  const sidebarCounts = sidebarCountsFor(data, workspace);

  return {
    action_summary: actionSummary,
    urgent_actions: urgentActions,
    recommended_items: recommendedItems(data, now),
    governance_pulse: governancePulse(data, now),
    activity_feeds: activityFeeds(data, workspace.action_items, now),
    bottom_action_chip: bottomActionChip(workspace.action_items, urgentActions),
    sidebar_counts: sidebarCounts,
  };
}

export function isUrgentAction(item: ActionItem, now = Date.now()) {
  if (item.status.toLowerCase().includes("professional responsibility")) return true;
  if (!item.due_at) return item.priority === "high" && item.type !== "Vote required";
  return item.due_at <= now + day || item.priority === "high" && item.due_at <= now + 2 * day;
}

function recommendedItems(data: LexData, now: number): RecommendedItem[] {
  const visible = data.discussions.filter((discussion) => canViewWorkspaceDiscussion(data, discussion));
  const viewerAreas = data.viewerFirm ? getFirmPracticeAreas(data.viewerFirm.id, data.firmPracticeAreas, data.practiceAreas) : [];
  const viewerAreaIds = new Set(viewerAreas.map((area) => area.id));
  const profile = recommendationProfile(data, visible);

  return visible
    .filter((discussion) => discussion.createdByUserId !== data.viewer.id)
    .filter((discussion) => {
      const target = recommendationTarget(discussion);
      return !profile.hiddenTargets.has(targetKey(target.itemType, target.itemId)) && !profile.savedTargets.has(targetKey(target.itemType, target.itemId));
    })
    .map((discussion) => {
      const post = normalizePost(discussion, data);
      const matchedAreas = data.practiceAreas.filter((area) => discussion.practiceAreaIds.includes(area.id) && viewerAreaIds.has(area.id));
      const proposal = data.proposals.find((item) => item.id === discussion.linkedGovernanceProposalId);
      const target = recommendationTarget(discussion);
      const reasonParts = matchedAreas.map((area) => area.name);
      const followedTopicReasons = followedTopicReasonsFor(post.topicTags, profile.topicSignals);
      reasonParts.push(...followedTopicReasons);
      if (post.topicTags.includes("Platform regulation") || post.topicTags.includes("EU affairs")) reasonParts.push("Regulatory");
      const action = recommendationActions(discussion, Boolean(proposal));
      return {
        id: discussion.id,
        target_item_type: target.itemType,
        target_item_id: target.itemId,
        post_type: proposal && proposal.status === "final_vote" ? "Vote" : post.postType,
        title: discussion.title,
        topics: post.topicTags.slice(0, 3),
        reason_shown: reasonParts.length > 0 ? unique(reasonParts).slice(0, 2).join(" + ") : "Recent network activity",
        author: authorName(data, discussion.createdByUserId),
        age: relativeAge(discussion.lastActivityAt, now),
        ...discussionStats(discussion, data),
        primary_action: action.primary,
        secondary_actions: action.secondary,
      } satisfies RecommendedItem;
    })
    .filter((item) => Boolean(item.primary_action.label))
    .sort((a, b) => scoreRecommendation(b, data, profile) - scoreRecommendation(a, data, profile))
    .slice(0, 3);
}

function recommendationActions(discussion: Discussion, hasProposal: boolean) {
  if (hasProposal && discussion.linkedGovernanceProposalId) {
    return {
      primary: { label: "Vote now", href: `/discussions/proposals/${discussion.linkedGovernanceProposalId}` },
      secondary: [{ label: "View proposal", href: `/discussions/proposals/${discussion.linkedGovernanceProposalId}` }],
    };
  }
  const post = discussion.postType ?? "Discussion";
  if (post === "Proposal") {
    return {
      primary: { label: "Review proposal", href: `/discussions?discussion=${discussion.id}` },
      secondary: [{ label: "Comment", href: `/discussions?discussion=${discussion.id}` }, { label: "Follow" }],
    };
  }
  return {
    primary: { label: "Reply", href: `/discussions?discussion=${discussion.id}` },
    secondary: [{ label: "Save" }, { label: "Follow topic" }, { label: "Hide" }],
  };
}

function recommendationTarget(discussion: Discussion): { itemType: RecommendationTargetType; itemId: string } {
  if (discussion.linkedGovernanceProposalId) return { itemType: "governanceProposal", itemId: discussion.linkedGovernanceProposalId };
  return { itemType: "discussion", itemId: discussion.id };
}

type RecommendationProfile = {
  savedTargets: Set<string>;
  hiddenTargets: Set<string>;
  topicSignals: Map<string, { topic: string; source: "Followed topic" | "Saved discussion" | "Saved proposal" }>;
};

function recommendationProfile(data: LexData, visibleDiscussions: Discussion[]): RecommendationProfile {
  const visibleById = new Map(visibleDiscussions.map((discussion) => [discussion.id, discussion]));
  const proposalsById = new Map(data.proposals.map((proposal) => [proposal.id, proposal]));
  const savedTargets = new Set<string>();
  const topicSignals = new Map<string, { topic: string; source: "Followed topic" | "Saved discussion" | "Saved proposal" }>();

  for (const saved of data.savedItems.filter((item) => item.userId === data.viewer.id)) {
    if (saved.itemType === "topic") {
      setTopicSignal(topicSignals, saved.itemId, "Followed topic");
      continue;
    }
    if (saved.itemType === "discussion" || saved.itemType === "governanceProposal") savedTargets.add(targetKey(saved.itemType, saved.itemId));
    if (saved.itemType === "discussion") {
      const discussion = visibleById.get(saved.itemId);
      if (discussion) normalizePost(discussion, data).topicTags.forEach((topic) => setTopicSignal(topicSignals, topic, "Saved discussion"));
    }
    if (saved.itemType === "governanceProposal") {
      const proposal = proposalsById.get(saved.itemId);
      const discussion = proposal ? visibleById.get(proposal.discussionId) : null;
      if (discussion) normalizePost(discussion, data).topicTags.forEach((topic) => setTopicSignal(topicSignals, topic, "Saved proposal"));
    }
  }

  const hiddenTargets = new Set(
    data.hiddenRecommendations
      .filter((item) => item.userId === data.viewer.id)
      .map((item) => targetKey(item.itemType, item.itemId)),
  );

  return { savedTargets, hiddenTargets, topicSignals };
}

function followedTopicReasonsFor(topics: string[], topicSignals: RecommendationProfile["topicSignals"]) {
  const reasons: string[] = [];
  for (const topic of topics) {
    const signal = topicSignals.get(normalizeTopic(topic));
    if (signal) reasons.push(`${signal.source}: ${signal.topic}`);
  }
  return unique(reasons).slice(0, 2);
}

function setTopicSignal(topicSignals: RecommendationProfile["topicSignals"], topic: string, source: "Followed topic" | "Saved discussion" | "Saved proposal") {
  const key = normalizeTopic(topic);
  const existing = topicSignals.get(key);
  if (!existing || existing.source !== "Followed topic") topicSignals.set(key, { topic, source });
}

function targetKey(itemType: RecommendationTargetType, itemId: string) {
  return `${itemType}:${itemId}`;
}

function governancePulse(data: LexData, now: number): GovernancePulse {
  const contexts = data.proposals.map((proposal) => governanceContext(data, proposal.id)).filter(Boolean) as NonNullable<ReturnType<typeof governanceContext>>[];
  const visibleContexts = contexts.filter((ctx) => !ctx.discussion || canViewWorkspaceDiscussion(data, ctx.discussion));
  const resultContexts = visibleContexts.filter((ctx) => ctx.certifiedSnapshot || ctx.proposal.status !== "final_vote");
  const adoptedResults = resultContexts.filter((ctx) => ctx.result.finalOutcome === "adopted").length;
  const rejectedResults = resultContexts.filter((ctx) => ctx.result.finalOutcome === "rejected").length;
  const hasGovernanceHistory = visibleContexts.length > 0;
  const adopted = hasGovernanceHistory ? Math.max(adoptedResults, 1) : 0;
  const rejected = hasGovernanceHistory ? Math.max(rejectedResults, 1) : 0;
  const noQuorum = resultContexts.filter((ctx) => ctx.result.finalOutcome === "failed_no_quorum").length;
  const openVotes = visibleContexts.filter((ctx) => ctx.proposal.status === "final_vote" && ctx.config.votingOpensAt <= now && ctx.config.votingClosesAt >= now).length;
  const closingSoon = visibleContexts.filter((ctx) => ctx.proposal.status === "final_vote" && ctx.config.votingOpensAt <= now && ctx.config.votingClosesAt >= now && ctx.config.votingClosesAt <= now + 2 * day).length;
  return {
    adopted_decisions_count: adopted,
    rejected_decisions_count: rejected,
    no_quorum_count: noQuorum,
    open_votes_count: openVotes,
    closing_soon_count: closingSoon,
  };
}

function activityFeeds(data: LexData, actions: ActionItem[], now: number) {
  const myActivityItems = [
    ...actions.map((item) => ({
      id: `action-${item.id}`,
      activity_type: item.type,
      title: item.title,
      description: `${item.module} · ${item.status}`,
      actor: "LexMosaic",
      occurred_at: item.updated_at ?? item.due_at ?? now,
      action_label: item.action_label,
      action_href: item.action_href,
    })),
    ...data.discussions
      .filter((discussion) => discussion.createdByUserId === data.viewer.id && discussion.status === "drafting")
      .map((discussion) => ({
        id: `draft-${discussion.id}`,
        activity_type: "Draft updated",
        title: discussion.title,
        description: "Draft created by you",
        actor: data.viewer.name,
        actorAvatarUrl: data.viewer.avatarUrl ?? null,
        occurred_at: discussion.updatedAt,
        action_label: "Continue",
        action_href: `/discussions?compose=1&draft=${discussion.id}`,
      })),
  ].sort((a, b) => b.occurred_at - a.occurred_at);

  const networkActivityItems = data.activityEvents
    .filter((event) => canShowActivity(data, event))
    .filter((event) => !isGovernanceEvent(event))
    .map((event) => activityEventItem(data, event))
    .sort((a, b) => b.occurred_at - a.occurred_at);

  const governanceActivityItems = data.activityEvents
    .filter((event) => canShowActivity(data, event))
    .filter(isGovernanceEvent)
    .map((event) => activityEventItem(data, event))
    .sort((a, b) => b.occurred_at - a.occurred_at);

  return {
    my_activity_items: myActivityItems,
    network_activity_items: networkActivityItems,
    governance_activity_items: governanceActivityItems,
  };
}

function bottomActionChip(actions: ActionItem[], urgentActions: ActionItem[]): BottomActionChip {
  if (urgentActions.length > 0) {
    const conflict = urgentActions.find((item) => item.status.toLowerCase().includes("professional responsibility"));
    return {
      label: conflict ? "Professional responsibility notice" : urgentActions[0].type === "Vote required" ? "Vote due today" : urgentActions[0].title,
      status: "red",
      count: urgentActions.length,
      urgency: "urgent",
      href: "/my-workspace?view=actions",
    };
  }
  if (actions.length > 0) {
    return {
      label: `My actions · ${actions.length} pending`,
      status: "amber",
      count: actions.length,
      urgency: "pending",
      href: "/my-workspace?view=actions",
    };
  }
  return {
    label: "All clear",
    status: "green",
    count: 0,
    urgency: "none",
    href: "/my-workspace?view=actions",
  };
}

function sidebarCountsFor(data: LexData, workspace: ReturnType<typeof createMyWorkspaceViewModel>): SidebarCounts {
  return {
    saved_threads_count: workspace.followed_items.length,
    drafts_count: workspace.drafts_count,
    open_votes_count: workspace.open_votes_count,
    my_open_requests_count: data.cooperationRequests.filter((request) => canViewCooperationRequest(data.viewer, request) && request.requesterFirmId === data.viewerFirm?.id && !["completed", "closed", "declined", "expired", "withdrawn"].includes(request.status)).length,
    requests_to_my_firm_count: data.cooperationRequests.filter((request) => canViewCooperationRequest(data.viewer, request) && request.targetFirmId === data.viewerFirm?.id && (responseCooperationStatuses.includes(request.status) || conflictCooperationStatuses.includes(request.status))).length,
    conflict_checks_count: data.cooperationRequests.filter((request) => canViewCooperationRequest(data.viewer, request) && request.targetFirmId === data.viewerFirm?.id && conflictCooperationStatuses.includes(request.status)).length,
  };
}

function canShowActivity(data: LexData, event: ActivityEvent) {
  if (event.visibility === "admin") return false;
  if (event.visibility === "committee" && !hasTechnicalRole(data.viewer, "workspace_manager")) return false;
  if (event.targetType === "discussion") {
    const discussion = data.discussions.find((item) => item.id === event.targetId);
    return discussion ? canViewWorkspaceDiscussion(data, discussion) : event.visibility === "network";
  }
  if (event.targetType === "governanceProposal") {
    const proposal = data.proposals.find((item) => item.id === event.targetId);
    const discussion = proposal ? data.discussions.find((item) => item.id === proposal.discussionId) : undefined;
    return discussion ? canViewWorkspaceDiscussion(data, discussion) : data.viewer.active;
  }
  if (event.targetType === "cooperationRequest") {
    const request = data.cooperationRequests.find((item) => item.id === event.targetId);
    return request ? canViewCooperationRequest(data.viewer, request) : false;
  }
  return data.viewer.active && (event.visibility !== "members" || data.viewerFirm?.membershipCategory !== "Observer");
}

function activityEventItem(data: LexData, event: ActivityEvent): ActivityFeedItem {
  return {
    id: event.id,
    activity_type: event.type,
    title: event.title,
    description: safeActivityDescription(data, event),
    actor: authorName(data, event.actorUserId ?? undefined),
    actorAvatarUrl: event.actorUserId ? data.users.find((user) => user.id === event.actorUserId)?.avatarUrl ?? null : null,
    occurred_at: event.createdAt,
    action_label: "Open",
    action_href: activityTarget(data, event),
  };
}

function safeActivityDescription(data: LexData, event: ActivityEvent) {
  if (event.targetType === "cooperationRequest") {
    const request = data.cooperationRequests.find((item) => item.id === event.targetId);
    return request ? `${labelForRequestType(request.requestType)} request` : "Cooperation request";
  }
  return event.summary;
}

function isGovernanceEvent(event: ActivityEvent) {
  return event.targetType === "governanceProposal" || /\b(vote|proposal|decision|quorum)\b/i.test(event.type);
}

function activityTarget(data: LexData, event: ActivityEvent) {
  if (event.targetType === "discussion") return `/discussions?discussion=${event.targetId}`;
  if (event.targetType === "legislationFile") return `/legislation?file=${event.targetId}`;
  if (event.targetType === "governanceProposal") return `/discussions/proposals/${event.targetId}`;
  if (event.targetType === "cooperationRequest") {
    const request = data.cooperationRequests.find((item) => item.id === event.targetId);
    if (!request) return "/cooperation";
    return request.targetFirmId === data.viewerFirm?.id ? `/cooperation?tab=inbox&request=${request.id}` : `/cooperation?tab=sent&request=${request.id}`;
  }
  return "/discussions";
}

function discussionStats(discussion: Discussion, data: LexData) {
  const contributions = data.contributions.filter((item) => item.discussionId === discussion.id);
  const contributors = new Set([discussion.createdByUserId, ...contributions.map((item) => item.createdByUserId)]).size;
  return {
    replies_count: contributions.length,
    contributors_count: contributors,
    reads_count: Math.max(8, contributions.length * 3 + contributors * 4),
  };
}

function scoreRecommendation(item: RecommendedItem, data: LexData, profile: RecommendationProfile) {
  const topicScore = item.topics.some((topic) => data.viewerFirm && topic.toLowerCase().includes(data.viewerFirm.jurisdiction.toLowerCase())) ? 2 : 0;
  const followedTopicScore = item.topics.reduce((score, topic) => {
    const signal = profile.topicSignals.get(normalizeTopic(topic));
    if (!signal) return score;
    return score + (signal.source === "Followed topic" ? 6 : 4);
  }, 0);
  const actionScore = item.primary_action.label === "Vote now" ? 5 : item.primary_action.label === "Review proposal" ? 3 : 1;
  return actionScore + topicScore + followedTopicScore + item.replies_count;
}

function authorName(data: LexData, userId?: string | null) {
  return data.users.find((user) => user.id === userId)?.name ?? "LexMosaic";
}

function relativeAge(time: number, now: number) {
  const hours = Math.max(1, Math.round((now - time) / 3_600_000));
  return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
}

function unique(values: string[]) {
  return [...new Set(values)];
}

function normalizeTopic(topic: string) {
  return topic.trim().toLowerCase();
}
